class HeaderThemeDetector {
	constructor(options = {}) {
		this.header = document.querySelector('#site-header');
		this.lastTheme = null;
		this.rafId = null;
		this.onThemeChange = options.onThemeChange || null;
		this.offset = -30;
		this.points = 5;
		this.rows = 1;
		this.lightThreshold = 0.4;
		this.imageRasterCache = new WeakMap();
		this.handleUpdate = () => this.requestUpdate();
	}

	init() {
		if (!this.header) {
			return;
		}

		window.addEventListener('load', this.handleUpdate);
		window.addEventListener('scroll', this.handleUpdate, {
			passive: true,
		});
		window.addEventListener('resize', this.handleUpdate);

		this.requestUpdate();
	}

	requestUpdate() {
		if (this.rafId !== null) {
			return;
		}

		this.rafId = window.requestAnimationFrame(() => {
			this.rafId = null;

			const theme = this.detectTheme();
			this.logTheme(theme);
		});
	}

	detectTheme() {
		const points = this.getSamplePoints();
		const previousPointerEvents = this.header.style.pointerEvents;
		let validPoints = 0;
		let lightPoints = 0;

		this.header.style.pointerEvents = 'none';

		for (const point of points) {
			const element = this.getTargetElement(point);
			const brightness = this.getElementBrightness(element, point);

			if (brightness === null) {
				continue;
			}

			validPoints += 1;

			if (brightness >= 186) {
				lightPoints += 1;
			}
		}

		this.header.style.pointerEvents = previousPointerEvents;

		if (!validPoints) {
			return 'dark';
		}

		return lightPoints / validPoints >= this.lightThreshold ? 'dark' : 'light';
	}

	getSamplePoints() {
		const rect = this.header.getBoundingClientRect();
		const points = [];
		const rowStep = Math.max(16, Math.round(rect.height * 0.35));

		for (let row = 0; row < this.rows; row += 1) {
			for (let index = 0; index < this.points; index += 1) {
				points.push({
					x: Math.round(rect.left + (rect.width * (index + 0.5)) / this.points),
					y: Math.round(rect.bottom + this.offset + row * rowStep),
				});
			}
		}

		return points;
	}

	getTargetElement({ x, y }) {
		return document.elementFromPoint(x, y);
	}

	getElementBrightness(element, point) {
		const imageBrightness = this.getImageBrightness(element, point);

		if (imageBrightness !== null) {
			return imageBrightness;
		}

		const backgroundColor = this.getResolvedBackgroundColor(element);

		if (!backgroundColor) {
			return null;
		}

		return this.getColorBrightness(backgroundColor);
	}

	getImageBrightness(element, point) {
		const image = this.getImageElement(element);

		if (!image || !image.complete || !image.naturalWidth || !image.naturalHeight) {
			return null;
		}

		try {
			const raster = this.getImageRaster(image);

			if (!raster) {
				return null;
			}

			const rect = image.getBoundingClientRect();
			const relativeX = (point.x - rect.left) / rect.width;
			const relativeY = (point.y - rect.top) / rect.height;
			const sampleX = Math.max(
				0,
				Math.min(raster.width - 1, Math.round(relativeX * (raster.width - 1)))
			);
			const sampleY = Math.max(
				0,
				Math.min(raster.height - 1, Math.round(relativeY * (raster.height - 1)))
			);

			const startX = Math.max(0, sampleX - 1);
			const startY = Math.max(0, sampleY - 1);
			const width = Math.min(3, raster.width - startX);
			const height = Math.min(3, raster.height - startY);
			let total = 0;
			let alphaTotal = 0;

			for (let y = startY; y < startY + height; y += 1) {
				for (let x = startX; x < startX + width; x += 1) {
					const index = (y * raster.width + x) * 4;
					const alpha = raster.data[index + 3] / 255;

					if (alpha === 0) {
						continue;
					}

					total +=
						((raster.data[index] * 299 +
							raster.data[index + 1] * 587 +
							raster.data[index + 2] * 114) /
							1000) *
						alpha;
					alphaTotal += alpha;
				}
			}

			return alphaTotal ? total / alphaTotal : null;
		} catch (error) {
			return null;
		}
	}

	getImageRaster(image) {
		if (this.imageRasterCache.has(image)) {
			return this.imageRasterCache.get(image);
		}

		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d', { willReadFrequently: true });

		if (!context) {
			return null;
		}

		canvas.width = 24;
		canvas.height = 24;
		context.drawImage(image, 0, 0, canvas.width, canvas.height);

		const raster = {
			data: context.getImageData(0, 0, canvas.width, canvas.height).data,
			width: canvas.width,
			height: canvas.height,
		};
		this.imageRasterCache.set(image, raster);

		return raster;
	}

	getImageElement(element) {
		if (element instanceof HTMLImageElement) {
			return element;
		}

		const picture = element?.closest('picture');
		return picture?.querySelector('img') || null;
	}

	getResolvedBackgroundColor(element) {
		let currentElement = element;

		while (currentElement) {
			const backgroundColor = window.getComputedStyle(currentElement).backgroundColor;

			if (!this.isTransparentColor(backgroundColor)) {
				return backgroundColor;
			}

			currentElement = currentElement.parentElement;
		}

		return null;
	}

	isTransparentColor(color) {
		if (!color) {
			return true;
		}

		if (color === 'transparent') {
			return true;
		}

		const values = this.parseColor(color);

		if (!values) {
			return true;
		}

		return values.a === 0;
	}

	isLightColor(color) {
		const brightness = this.getColorBrightness(color);
		return brightness !== null && brightness >= 186;
	}

	getColorBrightness(color) {
		const values = this.parseColor(color);

		if (!values) {
			return null;
		}

		return (values.r * 299 + values.g * 587 + values.b * 114) / 1000;
	}

	parseColor(color) {
		const match = color.match(/rgba?\(([^)]+)\)/i);

		if (!match) {
			return null;
		}

		const parts = match[1].split(',').map((value) => value.trim());

		if (parts.length < 3) {
			return null;
		}

		return {
			r: Number.parseFloat(parts[0]),
			g: Number.parseFloat(parts[1]),
			b: Number.parseFloat(parts[2]),
			a: parts[3] === undefined ? 1 : Number.parseFloat(parts[3]),
		};
	}

	logTheme(theme) {
		if (theme === this.lastTheme) {
			return;
		}

		this.lastTheme = theme;

		if (typeof this.onThemeChange === 'function') {
			this.onThemeChange(theme);
		}

		console.log(`header-theme-detector: ${theme}`);
	}
}

export default HeaderThemeDetector;
